import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  accountBalance,
  accountEffect,
  accountMovements,
  formatPeso,
  isTransferFee,
  movementKind,
  usedForSpending,
} from '@tahi/core';
import {
  adjustBalance,
  deleteInterest,
  deleteTransfer,
  recordInterest,
  restoreInterest,
  restoreTransfer,
  setUseForSpending,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import type { AccountEntry } from '../../../data/repos/budget';
import { Button, ScreenHeader, Sheet, showToast } from '../../../ui';
import { cn } from '../../../ui/cn';
import { Switch } from '../../../ui/Switch';
import { TextField } from '../../../ui/TextField';
import { sourceLabel } from '../../spending/format';
import { BigAmount, interestOf, savingsDetail, signedPeso } from '../components/MoneyCards';
import { copy, errorText } from '../copy';
import { dayShort, parsePesoInput } from '../format';
import { useBudgetToday, useToday } from '../hooks';
import { CARD, FOCUS, QUIET_LINK, SCREEN } from '../styles';
import { parseSignedPeso, signedInput } from './AccountFormRoute';

/** Where a movement links to: its own screen when it has one. */
function hrefOf(e: AccountEntry, receivableIds: ReadonlySet<string>): string | null {
  const k = movementKind(e);
  if (k === 'income')
    return e.sourceModule === 'budget' && !e.sourceId
      ? `/m/budget/income/other/${e.id}`
      : '/m/budget/income';
  if (k === 'deposit') {
    if (!e.sourceId) return null;
    return receivableIds.has(e.sourceId) ? '/m/budget/owed' : `/m/budget/expected/${e.sourceId}`;
  }
  if (k === 'expense' || k === 'refund')
    return isTransferFee(e) ? null : `/m/spending/entries/${e.id}`;
  return null;
}

export function AccountDetailScreen() {
  const { id = '' } = useParams();
  const env = useCommandEnv();
  const s = useBudgetToday();
  const [adjusting, setAdjusting] = useState(false);
  const [recording, setRecording] = useState(false);
  if (!s) {
    return (
      <main className={SCREEN}>
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const account = s.accounts.find((a) => a.id === id);
  if (!account) {
    return (
      <main className={SCREEN}>
        <p className="text-muted">{errorText('not_found')}</p>
      </main>
    );
  }
  const balance = accountBalance(account, s.accountEntries, s.today);
  const nameOf = (aid: string | null) => (aid && s.accounts.find((a) => a.id === aid)?.name) || '—';
  const rows = accountMovements(account, s.accountEntries, s.today).sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : b.createdAt.localeCompare(a.createdAt),
  );

  const receivableIds = new Set(s.receivables.map((r) => r.id));
  const interest = interestOf(s, id);
  const detail = savingsDetail(account);
  const spending = usedForSpending(account);

  async function remove(e: AccountEntry) {
    const isInterest = movementKind(e) === 'interest';
    const r = isInterest ? await deleteInterest(env, e.id) : await deleteTransfer(env, e.id);
    if (r.ok) {
      showToast({
        message: copy.deletedMovement,
        action: {
          label: copy.undo,
          onPress: () =>
            void (isInterest ? restoreInterest(env, e.id) : restoreTransfer(env, e.id)),
        },
      });
    }
  }

  return (
    <main className={SCREEN}>
      <ScreenHeader
        title={account.name}
        back={{ to: '/m/budget/accounts', label: copy.accounts }}
        actions={
          <Link to={`/m/budget/accounts/${id}/edit`} className={QUIET_LINK}>
            {copy.editAccount}
          </Link>
        }
      />
      <section className={`${CARD} flex flex-col gap-1 [container-type:inline-size]`}>
        <BigAmount minor={balance} maxPx={40} testId="account-balance" />
        <p className="text-sm text-muted">
          {copy.openingLine(account.openingBalanceMinor, dayShort(account.openingDate))}
        </p>
        {detail ? <p className="text-sm text-muted">{detail}</p> : null}
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-fg">{copy.useForSpending}</span>
          <Switch
            label={copy.useForSpending}
            checked={spending}
            onCheckedChange={(on) =>
              void setUseForSpending(env, id, on).then(
                (r) => r.ok && showToast({ message: copy.turnedSpending(account.name, on) }),
              )
            }
          />
        </div>
        <p className="text-sm text-muted">{spending ? copy.spendingOn : copy.spendingOff}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setAdjusting(true)}>
            {copy.adjustBalance}
          </Button>
          {account.annualRateBp ? (
            <Button variant="secondary" onClick={() => setRecording(true)}>
              {copy.recordInterest}
            </Button>
          ) : null}
          {s.accounts.length >= 2 ? (
            <Link to={`/m/budget/transfer?from=${id}`} className={QUIET_LINK}>
              {copy.transfer}
            </Link>
          ) : null}
        </div>
      </section>

      {interest ? (
        <section aria-label={copy.interestEstimated} className={`${CARD} flex flex-col gap-1`}>
          <dl className="divide-y divide-divider">
            <div className="flex min-h-11 items-center justify-between gap-3">
              <dt className="text-fg">{copy.earnedSoFar}</dt>
              <dd data-testid="interest-earned" className="font-mono tabular-nums text-fg">
                {formatPeso(interest.earnedMinor, { cents: 'auto' })}
              </dd>
            </div>
            {interest.atMaturityMinor !== null ? (
              <div className="flex min-h-11 items-center justify-between gap-3">
                <dt className="text-fg">
                  {interest.matured ? copy.matured : copy.valueAtMaturity}
                </dt>
                <dd data-testid="value-at-maturity" className="font-mono tabular-nums text-fg">
                  {formatPeso(interest.atMaturityMinor, { cents: 'auto' })}
                </dd>
              </div>
            ) : null}
            {!interest.matured ? (
              <div className="flex min-h-11 items-center justify-between gap-3">
                <dt className="text-fg">{copy.valueIn12}</dt>
                <dd data-testid="value-in-12" className="font-mono tabular-nums text-fg">
                  {formatPeso(interest.in12MonthsMinor, { cents: 'auto' })}
                </dd>
              </div>
            ) : null}
            {interest.receivedMinor > 0 ? (
              <div className="flex min-h-11 items-center justify-between gap-3">
                <dt className="text-fg">{copy.interestReceivedTotal}</dt>
                <dd className="font-mono tabular-nums text-success">
                  +{formatPeso(interest.receivedMinor, { cents: 'auto' })}
                </dd>
              </div>
            ) : null}
          </dl>
          <p className="text-sm text-muted">{copy.estimateNote}</p>
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-[20px] font-bold text-fg">{copy.movements}</h2>
        {rows.length === 0 ? (
          <p className="text-muted">{copy.noMovements}</p>
        ) : (
          <ul aria-label={copy.movements} className="divide-y divide-divider">
            {rows.map((e) => {
              const k = movementKind(e);
              const effect = accountEffect(e, id);
              const other =
                k === 'transfer'
                  ? e.accountId === id
                    ? `${copy.to.toLowerCase()} ${nameOf(e.toAccountId)}`
                    : `${copy.from.toLowerCase()} ${nameOf(e.accountId)}`
                  : null;
              const label = e.note ?? sourceLabel(e.sourceModule);
              const href = hrefOf(e, receivableIds);
              const deletable =
                e.sourceModule === 'budget' &&
                (k === 'transfer' || k === 'adjustment' || k === 'interest');
              const body = (
                <span className="min-w-0 text-fg">
                  {k === 'transfer' ? (e.note ?? copy.movementLabels.transfer) : label}
                  <span className="block text-sm text-muted">
                    {copy.movementLabels[k]}
                    {other ? ` ${other}` : ''} · {dayShort(e.date)}
                  </span>
                </span>
              );
              return (
                <li key={e.id} className="flex min-h-11 items-center justify-between gap-3 py-1">
                  {href ? (
                    <Link to={href} className={`min-w-0 ${FOCUS}`}>
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                  <span className="flex shrink-0 items-center gap-1">
                    <span
                      className={cn(
                        'font-mono tabular-nums',
                        effect > 0 ? 'text-success' : 'text-fg',
                      )}
                    >
                      {effect > 0 ? '+' : ''}
                      {signedPeso(effect)}
                    </span>
                    {deletable ? (
                      <button
                        type="button"
                        aria-label={`${copy.delete}: ${copy.movementLabels[k]} ${formatPeso(e.amountMinor, { cents: 'auto' })}`}
                        onClick={() => void remove(e)}
                        className={`${QUIET_LINK} px-2`}
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {recording ? (
        <InterestSheet
          onClose={() => setRecording(false)}
          onSave={async (amountMinor, date) => {
            const r = await recordInterest(env, id, { amountMinor, date });
            if (!r.ok) return errorText(r.error);
            const entryId = r.id;
            showToast({
              message: copy.recordedInterest(amountMinor, account.name),
              action: { label: copy.undo, onPress: () => void deleteInterest(env, entryId) },
            });
            setRecording(false);
            return null;
          }}
        />
      ) : null}
      {adjusting ? (
        <AdjustSheet
          accountId={id}
          balance={balance}
          hasRate={Boolean(account.annualRateBp)}
          onClose={() => setAdjusting(false)}
          onSave={async (real) => {
            const r = await adjustBalance(env, id, real);
            if (!r.ok) return errorText(r.error);
            if (r.id) {
              const entryId = r.id;
              showToast({
                message: copy.adjusted(real - balance),
                action: { label: copy.undo, onPress: () => void deleteTransfer(env, entryId) },
              });
            }
            setAdjusting(false);
            return null;
          }}
        />
      ) : null}
    </main>
  );
}

function InterestSheet({
  onClose,
  onSave,
}: {
  onClose(): void;
  onSave(amountMinor: number, date: string): Promise<string | null>;
}) {
  const today = useToday();
  const [text, setText] = useState('');
  const [date, setDate] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const minor = parsePesoInput(text);
  return (
    <Sheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={copy.recordInterest}
      description={copy.recordInterestHelp}
      footer={
        <Button
          variant="primary"
          block
          disabled={minor === null}
          onClick={() => {
            if (minor === null) return;
            void onSave(minor, date).then(setError);
          }}
        >
          {copy.save}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField
          label={copy.amount}
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <TextField
          label={copy.receivedOn}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

function AdjustSheet({
  balance,
  hasRate,
  onClose,
  onSave,
}: {
  accountId: string;
  balance: number;
  /** An account with a rate gets a pointer to "Interest received". */
  hasRate: boolean;
  onClose(): void;
  onSave(real: number): Promise<string | null>;
}) {
  const [text, setText] = useState(signedInput(balance));
  const [error, setError] = useState<string | null>(null);
  const real = parseSignedPeso(text);
  return (
    <Sheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={copy.adjustBalance}
      footer={
        <Button
          variant="primary"
          block
          disabled={real === null}
          onClick={() => {
            if (real === null) return;
            void onSave(real).then(setError);
          }}
        >
          {copy.save}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">{copy.adjustHelp(balance)}</p>
        {hasRate ? <p className="text-sm text-muted">{copy.adjustInterestHint}</p> : null}
        <TextField
          label={copy.realBalance}
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
